import React, { useState, useEffect } from 'react';
import matchedIcon from '../../assets/location-image/matched.png';
import notMatchedIcon from '../../assets/location-image/notmatched.png';

function CurrentLocation({ data = {}, handleSave }) {
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [error, setError] = useState('');
  const [distance, setDistance] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    fetchLocation();
  }, []);

  const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const fetchLocation = () => {
    setError('');
    setShowErrorPopup(false);
    setIsLoading(true);
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      setShowErrorPopup(true);
      setIsLoading(false);
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude.toFixed(6));
        const lon = Number(pos.coords.longitude.toFixed(6));

        // Calculate distance to industry location
        let distanceToIndustry = null;
        if (data.latitude && data.longitude) {
          distanceToIndustry = haversine(lat, lon, Number(data.latitude), Number(data.longitude));
        }

        setLatitude(lat);
        setLongitude(lon);
        if (distanceToIndustry !== null) {
          setDistance(Number(distanceToIndustry.toFixed(3)));
        }

        // Stop watching after first successful read
        navigator.geolocation.clearWatch(watchId);
        setIsLoading(false);


      },
      (err) => {
        setError(err.message || 'Unable to get location');
        setShowErrorPopup(true);
        navigator.geolocation.clearWatch(watchId);
        setIsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );

  };

  const onSubmit = async () => {
    // Check if student is within 500 meters of industry location
    if (data.latitude && data.longitude && distance !== null) {
      const distanceInMeters = distance * 1000; // Convert km to meters
      if (distanceInMeters > 500) {
        setError(`You are ${distanceInMeters.toFixed(0)} meters away from the industry location. Please get within 50 meters to submit.`);
        return;
      }
    }
    // Start submitting
    setIsSubmitting(true);

    try {
      await handleSave?.({ ...data, latitude, longitude });
    } catch (error) {
      console.error("Error submitting location:", error);
    } finally {
      // Reset submitting state
      setIsSubmitting(false);
    }
  };

  // Check if submit button should be disabled
  const isSubmitDisabled =
    !data.latitude ||
    !data.longitude ||
    distance === null ||
    distance * 1000 > 500 ||
    isSubmitting;

  // Check if input boxes should be disabled
  const areInputsDisabled =
    !data.latitude ||
    !data.longitude ||
    distance === null ||
    distance * 1000 > 500 ||
    isSubmitting;

  return (
    <div className="flex flex-col justify-center items-center h-full">
      {/* Loading Spinner */}
      {isLoading && !(data.latitude && data.longitude && distance !== null) && (
        <div className="flex flex-col items-center justify-center gap-4 mb-6">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-600">Fetching your location...</p>
        </div>
      )}

      {/* Error Popup Modal */}
      {showErrorPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-lg">
            <h2 className="text-lg font-semibold text-red-600 mb-4">Location Error</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowErrorPopup(false)}
                className="py-2 px-4 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Dismiss
              </button>
              <button
                onClick={() => {
                  setShowErrorPopup(false);
                  fetchLocation();
                }}
                className="py-2 px-4 bg-primary text-white rounded-md hover:bg-primary/90"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Form */}
      <>
        {distance !== null && (
          <div className="w-full text-center mb-4">

            <p className={`text-xs mt-1 ${distance * 1000 <= 500 ? 'text-green-600' : 'text-orange-600'
              }`}>
              {distance * 1000 <= 500
                ? '✓ You are within 50 meters - Ready to submit'
                : <>
                  You are very far from your industry, <br />
                  ⚠ Please move closer to the industry location
                </>}
            </p>
          </div>
        )}

        {/* Two Column Layout: Left - Image (Web), Right - Form Fields (Web) */}
        <div className="w-full flex flex-col md:flex-row gap-4 md:gap-6">
          {/* Image - Left on Web, Top on Mobile - Always rendered to prevent layout shift */}
          <div className="w-full md:w-1/2 flex items-center justify-center order-1 md:order-1">
            {distance !== null && data.latitude && data.longitude && latitude && longitude && (
              <>
                {distance * 1000 <= 500 ? (
                  <img
                    src={matchedIcon}
                    alt="Location Matched"
                    className="w-full h-auto max-w-[150px] md:max-w-[200px] object-contain"
                  />
                ) : (
                  <img
                    src={notMatchedIcon}
                    alt="Location Not Matched"
                    className="w-full h-auto max-w-[150px] md:max-w-[200px] object-contain"
                  />
                )}
              </>
            )}
          </div>

          {/* Form Fields - Right on Web, Bottom on Mobile */}
          <div className="w-full md:w-1/2 flex flex-col gap-2 order-2 md:order-2">
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">Latitude</label>
              <input
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="Latitude"
                disabled={areInputsDisabled}
                className={`border rounded-md px-3 py-2 mb-5 text-sm w-full outline-none ${areInputsDisabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                  }`}
              />
            </div>
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">Longitude</label>
              <input
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="Longitude"
                disabled={areInputsDisabled}
                className={`border placeholder:text-gray-300 rounded-md px-3 py-2 mb-5 text-sm w-full outline-none ${areInputsDisabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                  }`}
              />
            </div>
            <button
              onClick={onSubmit}
              disabled={isSubmitDisabled}
              className={`py-2 px-8 focus:ring-offset-blue-200 w-full transition ease-in duration-200 text-center text-base font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-md cursor-pointer select-none ${isSubmitDisabled
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary/90 text-white'
                }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Submitting...
                </div>
              ) : distance !== null && data.latitude && data.longitude && distance * 1000 > 500
                ? `Submit (Get within 50m)`
                : 'Submit'}
            </button>
          </div>
        </div>
      </>
    </div>
  );
}

export default CurrentLocation;